// One organisation (spec/14 §3.1, F-HQ-02): an overview page. It opens with a hero — the organisation's name, what
// kind it is, its status, and four figures (venues, open now, setup steps, owner) — then how ready it is to open and
// the one next step, its owner, its details, and the ways on.
//
// **The record is read only here.** The owner completes or corrects the legal name, PAN and registered
// address in their own settings (ADR-0046); head office editing the record (`PATCH`: the kind or the HQ
// access level, audited before and after) is not built yet, and the page says so (in an info tip) rather than
// offering an edit that does not exist. What the owner has not recorded is not a row of "Not recorded": the
// details leave it out and name it once, in a notice.
//
// **The owner** is its own component (`OwnerInvitationPanel.tsx`, ADR-0046): the api says whether the
// owner has joined, and the panel offers a fresh invitation, the link and withdrawing one while they have
// not. An `hq_admin` installing the first owner is intended (the product owner, 2026-09-22).
//
// Its venues are a link to the Venues list filtered to it rather than a list on this page: reading
// them is audited in the organisation's log (spec/14 §3.3), so it happens when head office asks.
// The figures are what the setup read (`GET /v1/hq/setup`) already says; players and sales are not stats here
// until the api offers them cheaply.

import type { Address, HqOrganization } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { ActionCard, ActionGrid, Button, FactGrid, HeroStat, InfoTip, PageHero, PageSkeleton, TextField, type FactItem } from '@clavian/ui-web';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { LoadFailure } from '../../shell/LoadFailure';
import { NavIcon } from '../../shell/nav-icons';
import { NotPermitted, RequirePlatformPermission, usePlatformAccessHere } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_ORGANIZATIONS, HQ_PAYMENTS, HQ_SETTINGS, HQ_SUBSCRIPTIONS } from '../sections';
import { AddonsPanel } from './AddonsPanel';
import { OrganizationStatus } from './OrganizationsPage';
import { OwnerInvitationPanel } from './OwnerInvitationPanel';
import { PaymentsPanel } from './PaymentsPanel';
import { useDecideOrganization, useOrganization, useOrganizationOwner } from './queries';
import { OrganizationSetupTracker, useHqOrganizationSetup } from './SetupProgress';

export function OrganizationPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <OrganizationView />
    </RequirePlatformPermission>
  );
}

function OrganizationView() {
  const { t } = useTranslation('hq');
  const params = useParams({ strict: false }) as { organizationId?: string };
  const organizationId = params.organizationId ?? '';
  const organization = useOrganization(organizationId);

  if (organization.data === undefined) {
    if (organization.isError && problemCode(organization.error) === PROBLEM.forbidden) return <NotPermitted />;
    // No such organisation, or an id the api does not take: said as that, not as a failure to retry.
    const code = problemCode(organization.error);
    if (organization.isError && (code === PROBLEM.notFound || code === PROBLEM.validationFailed)) {
      return (
        <div className="console-page">
          <Link className="console-hq-back" to={HQ_PATHS.organizations}>
            {t('organizations.page.back')}
          </Link>
          <h1 className="console-page__title">{t('organizations.title')}</h1>
          <p role="alert">{t('organizations.page.noSuch')}</p>
        </div>
      );
    }
    return organization.isError ? (
      <LoadFailure error={organization.error} message={t('organizations.page.unavailable')} onRetry={() => void organization.refetch()} />
    ) : (
      <PageSkeleton />
    );
  }

  return <OrganizationOverview record={organization.data} />;
}

/** The overview: a hero with its figures, how ready it is to open, its owner, its details, and the ways on. */
function OrganizationOverview({ record }: { readonly record: HqOrganization }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const access = usePlatformAccessHere();
  const { setup } = useHqOrganizationSetup(record.id);
  const owner = useOrganizationOwner(record.id);
  const venues = setup?.venues ?? [];
  const open = venues.filter((venue) => venue.status === 'live').length;
  const dash = '—';
  const venuesSearch: Readonly<Record<string, string>> = { organizationId: record.id };
  // What head office can do next: while the owner has not joined, the owner card holds the action; after, the venues.
  const trackerAction =
    owner.data === undefined ? undefined : owner.data.status === 'joined' ? (
      <Button asChild variant="primary">
        <Link to={HQ_PATHS.venues} search={venuesSearch}>
          {t('organizations.page.toVenues')}
        </Link>
      </Button>
    ) : (
      <Button variant="primary" onClick={() => document.getElementById('console-hq-owner')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
        {t('organizations.page.toOwner')}
      </Button>
    );

  return (
    <div className="console-page">
      <PageHero
        breadcrumb={<Link to={HQ_PATHS.organizations}>{t('organizations.page.back')}</Link>}
        tileName={record.name}
        title={record.name}
        meta={t('organizations.page.meta', {
          kind: t(`organizations.kind.${record.kind}`),
          currency: record.currency,
          date: format.date(record.createdAt, 'medium'),
        })}
        status={<OrganizationStatus status={record.status} />}
        stats={
          <>
            <HeroStat label={t('organizations.page.stat.venues')} value={setup === undefined ? dash : venues.length} />
            <HeroStat
              label={t('organizations.page.stat.open')}
              value={setup === undefined ? dash : open}
              {...(setup === undefined ? {} : { of: t('organizations.page.stat.of', { total: venues.length }) })}
            />
            <HeroStat
              label={t('organizations.page.stat.steps')}
              value={setup === undefined ? dash : setup.essentialsDone}
              {...(setup === undefined ? {} : { of: t('organizations.page.stat.of', { total: setup.essentialsTotal }) })}
            />
            <HeroStat label={t('organizations.page.stat.owner')} value={owner.data === undefined ? dash : t(`organizations.ownerStatus.${owner.data.status}`)} />
          </>
        }
      />
      {record.status === 'pending' ? <ApprovalPanel organization={record} /> : null}
      <OrganizationSetupTracker organizationId={record.id} action={trackerAction} />
      <OwnerInvitationPanel organizationId={record.id} />
      {access.permissions.has(HQ_PAYMENTS) ? <PaymentsPanel organizationId={record.id} /> : null}
      {/* Its add-ons — Clavian Boot for N PCs (spec/14 §4.5, F-BOOT-17): `hq:subscriptions` gates every route the panel calls. */}
      {access.permissions.has(HQ_SUBSCRIPTIONS) ? <AddonsPanel organizationId={record.id} currency={record.currency} /> : null}
      <RecordPanel organization={record} />
      <ElsewherePanel organizationId={record.id} />
    </div>
  );
}

/**
 * The Provider's decision on a `pending` organisation (ADR-0019 §6, F-HQ-02): Approve it into `active`,
 * or Decline it (with a reason) into `suspended`. Only shown while it is pending; once decided, the
 * record's status re-reads and this panel goes. A decline asks for its reason before it is sent.
 */
function ApprovalPanel({ organization }: { readonly organization: HqOrganization }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const decide = useDecideOrganization(organization.id);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  const onError = (caught: unknown): void => {
    setError(problemCode(caught) === PROBLEM.conflict ? t('organizations.approval.notPending') : problems.text(caught, { fallback: t('organizations.approval.failed') }));
  };

  function approve(): void {
    setError(undefined);
    decide.mutate({ decision: 'approve' }, { onError });
  }

  function decline(): void {
    if (reason.trim() === '') {
      setError(t('organizations.approval.reasonRequired'));
      return;
    }
    setError(undefined);
    decide.mutate({ decision: 'decline', reason: reason.trim() }, { onError });
  }

  return (
    <section className="console-hq-panel" aria-labelledby="console-hq-approval">
      <h2 id="console-hq-approval" className="console-hq-owner__title">
        <span>{t('organizations.approval.title')}</span>
        <InfoTip>{t('organizations.approval.intro')}</InfoTip>
      </h2>
      {error !== undefined ? (
        <div className="console-alert" role="alert">
          <p>{error}</p>
        </div>
      ) : null}
      {declining ? (
        <>
          <TextField
            label={t('organizations.approval.reasonLabel')}
            description={t('organizations.approval.reasonHint')}
            required
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          <div className="console-form__actions">
            <Button variant="ghost" onClick={() => setDeclining(false)}>
              {t('organizations.approval.cancel')}
            </Button>
            <Button variant="danger" busy={decide.isPending} onClick={decline}>
              {t('organizations.approval.decline')}
            </Button>
          </div>
        </>
      ) : (
        <div className="console-form__actions">
          <Button variant="secondary" busy={decide.isPending} onClick={() => setDeclining(true)}>
            {t('organizations.approval.decline')}
          </Button>
          <Button variant="primary" busy={decide.isPending} onClick={approve}>
            {t('organizations.approval.approve')}
          </Button>
        </div>
      )}
    </section>
  );
}

function RecordPanel({ organization }: { readonly organization: HqOrganization }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  // Only what is recorded is a fact; what is not is named once, in the notice. The trade name and the status are the hero's.
  const items: readonly FactItem[] = [
    { key: 'slug', label: t('organizations.page.field.slug'), value: organization.slug },
    { key: 'legalName', label: t('organizations.page.field.legalName'), value: organization.legalName },
    { key: 'kind', label: t('organizations.page.field.kind'), value: t(`organizations.kind.${organization.kind}`) },
    { key: 'pan', label: t('organizations.page.field.pan'), value: organization.pan === null ? null : <span className="console-code">{organization.pan}</span> },
    {
      key: 'registeredAddress',
      label: t('organizations.page.field.registeredAddress'),
      value: organization.registeredAddress === null ? null : <AddressLines address={organization.registeredAddress} />,
    },
    { key: 'hqAccess', label: t('organizations.page.field.hqAccess'), value: t(`organizations.hqAccess.${organization.hqAccess}`) },
    { key: 'currency', label: t('organizations.page.field.currency'), value: organization.currency },
    { key: 'createdAt', label: t('organizations.page.field.createdAt'), value: format.dateTime(organization.createdAt, { date: 'medium', time: 'short' }) },
    { key: 'id', label: t('organizations.page.field.id'), value: <span className="console-code">{organization.id}</span> },
  ];
  const missing = (['legalName', 'pan', 'registeredAddress'] as const).filter((key) => organization[key] === null);
  const names = missing.map((key) => t(`organizations.page.missingWhat.${key}`));
  const what = names.length > 1 ? `${names.slice(0, -1).join(', ')} ${t('organizations.page.and')} ${names.at(-1) ?? ''}` : names.join('');
  return (
    <section className="console-hq-panel" aria-labelledby="console-hq-record">
      <h2 id="console-hq-record" className="console-hq-owner__title">
        <span>{t('organizations.page.details')}</span>
        <InfoTip>{t('organizations.page.readOnly')}</InfoTip>
      </h2>
      <FactGrid items={items} {...(missing.length === 0 ? {} : { missing: t('organizations.page.missing', { what }) })} />
    </section>
  );
}

/** A postal address, a line at a time, the country by its name in the reader's language. */
function AddressLines({ address }: { readonly address: Address }) {
  const { i18n } = useTranslation('hq');
  const country = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language, 'en'], { type: 'region', fallback: 'code' }).of(address.country) ?? address.country;
    } catch {
      return address.country;
    }
  }, [i18n.language, address.country]);
  const lines = [
    address.line1,
    address.line2,
    address.landmark,
    `${address.locality}, ${address.region} ${address.postalCode}`,
    country,
  ].filter((line): line is string => line !== undefined && line !== '');
  return (
    <span className="console-hq-lines">
      {lines.map((line, index) => (
        <span key={index}>{line}</span>
      ))}
    </span>
  );
}

function ElsewherePanel({ organizationId }: { readonly organizationId: string }) {
  const { t } = useTranslation('hq');
  const access = usePlatformAccessHere();
  const params: Readonly<Record<string, string>> = { organizationId };
  const search: Readonly<Record<string, string>> = { organizationId };
  return (
    <section className="console-hq-panel" aria-labelledby="console-hq-elsewhere">
      <h2 id="console-hq-elsewhere" className="console-hq-owner__title">
        <span>{t('organizations.page.elsewhere')}</span>
        <InfoTip>{t('organizations.page.elsewhereTip')}</InfoTip>
      </h2>
      <ActionGrid label={t('organizations.page.elsewhere')}>
        <ActionCard
          icon={<NavIcon name="venues" />}
          title={t('organizations.page.venues')}
          description={t('organizations.page.venuesHint')}
          render={(parts) => <Link to={HQ_PATHS.venues} search={search} {...parts} />}
        />
        {/* Moving its players from the software it used before (spec/14 §3.5, F-HQ-40): `hq:organizations`, as this page. */}
        <ActionCard
          icon={<NavIcon name="upload" />}
          title={t('migration.link')}
          description={t('migration.linkHint')}
          render={(parts) => <Link to={HQ_PATHS.organizationMigration} params={params} {...parts} />}
        />
        {/* One organisation's settings is Platform settings' page, and `hq:settings`'s alone (spec/14 §14). */}
        {access.permissions.has(HQ_SETTINGS) ? (
          <ActionCard
            icon={<NavIcon name="settings" />}
            title={t('organizations.page.settings')}
            description={t('organizations.page.settingsHint')}
            render={(parts) => <Link to={HQ_PATHS.organizationSettings} params={params} {...parts} />}
          />
        ) : null}
      </ActionGrid>
    </section>
  );
}
